import React, { useState } from "react";
import type { EmailItem, Attachment } from "src/types/conditionsInbox";
import { ChevronPaginationLeftIcon } from "src/assets/icons";
import { PrimaryButton } from "src/components/Buttons";
import { AttachmentModal } from "./AttachmentModal";
import {
  DetailContainer,
  BackButton,
  EmailDetails,
  SubjectHeader,
  DetailsList,
  DetailRow,
  DetailLabel,
  DetailValue,
  MessageSection,
  MessageLabel,
  MessageContent,
  ActionButtonContainer,
} from "./EmailDetailSection.styles";
import { Attachments } from "./Attachments";

interface EmailDetailSectionProps {
  email: EmailItem;
  attachments: Attachment[];
  onBack: () => void;
  onMarkAsJunk: (emailId: string) => void;
  updateAttachmentName?: (attachmentId: string, newName: string) => void;
  fetchAttachmentSignedUrl?: (attachmentId: string) => Promise<string>;
  renamedAttachments?: Record<string, string>;
}

export const EmailDetailSection: React.FC<EmailDetailSectionProps> = ({
  email,
  attachments,
  onBack,
  onMarkAsJunk,
  updateAttachmentName,
  fetchAttachmentSignedUrl,
  renamedAttachments = {},
}) => {
  const [selectedAttachment, setSelectedAttachment] =
    useState<Attachment | null>(null);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const handleCloseModal = () => {
    setSelectedAttachment(null);
  };

  const handleRenameAttachment = (newName: string) => {
    if (selectedAttachment) {
      updateAttachmentName?.(selectedAttachment.id, newName);
      handleCloseModal();
    }
  };

  const handleClickAttachment = (event: React.MouseEvent<HTMLDivElement>) => {
    const { id } = event.currentTarget.dataset;
    const attachment = attachments.find((a) => a.id === id);
    setSelectedAttachment(attachment || null);
  };

  const decodeHtmlEntities = (text: string) => {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = text;
    return textarea.value;
  };

  return (
    <DetailContainer>
      <ActionButtonContainer>
        <BackButton onClick={onBack}>
          <ChevronPaginationLeftIcon />
          <span>Back</span>
        </BackButton>
        <PrimaryButton
          variant="delete"
          onClick={() => onMarkAsJunk(email.id)}
          disabled={email.status === "junk"}
        >
          {email.status === "junk" ? "Marked as Junk" : "Mark as Junk"}
        </PrimaryButton>
      </ActionButtonContainer>

      <EmailDetails>
        <SubjectHeader>{email.subject}</SubjectHeader>

        <DetailsList>
          <DetailRow>
            <DetailLabel>From:</DetailLabel>
            <DetailValue>
              {email.sender} ({email.senderEmail})
            </DetailValue>
          </DetailRow>

          <DetailRow>
            <DetailLabel>Date:</DetailLabel>
            <DetailValue>{formatDate(email.receivedAt)}</DetailValue>
          </DetailRow>

          {email.body && (
            <MessageSection>
              <MessageLabel>Message:</MessageLabel>
              <MessageContent>{decodeHtmlEntities(email.body)}</MessageContent>
            </MessageSection>
          )}
        </DetailsList>
      </EmailDetails>

      <Attachments
        attachments={attachments}
        renamedAttachments={renamedAttachments}
        onClickAttachment={handleClickAttachment}
      />

      {selectedAttachment && (
        <AttachmentModal
          isOpen={!!selectedAttachment}
          onClose={handleCloseModal}
          attachment={selectedAttachment}
          onRename={handleRenameAttachment}
          fetchAttachmentSignedUrl={fetchAttachmentSignedUrl}
        />
      )}
    </DetailContainer>
  );
};
